import {
  COUPON_CAMPAIGN_STATUS_LABELS,
  COUPON_CAMPAIGN_STATUSES,
  COUPON_ISSUANCE_LABELS,
  type CouponCampaign,
  couponCampaignStatusSchema,
} from "@station/contracts";
import { formatTrDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Search, TicketPercent } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  CancelCouponDialog,
  CouponCampaignDialog,
  CouponCustomerDialog,
  CouponDetailView,
} from "../../../../features/loyalty/coupon-dialogs.js";
import {
  useCouponCampaigns,
  useCouponLookup,
  useLoyaltyPermissions,
} from "../../../../features/loyalty/queries.js";
import { CampaignStatusBadge, LoyaltyCompanyPage } from "../../../../features/loyalty/shared.js";
import { validToDate } from "../../../../features/pos/pos-values.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatCount } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...companySearchShape,
  status: couponCampaignStatusSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/sadakat/kuponlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CouponsRoute,
});

/**
 * Kupon şablonları (ADR 0017 §23, §25): şirket bazında liste (kod istatistiğiyle), yeni şablon ve KOD SORGULAMA (kasadan
 * gelen soru: "bu kupon geçerli mi, kime ait, ne zaman kullanıldı?"). Ayrıntıda kodlar, üretim, dışa aktarım, müşteriye
 * verme / atama ve iptal.
 */
function CouponsRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useLoyaltyPermissions();
  const [creating, setCreating] = useState(false);
  return (
    <LoyaltyCompanyPage
      title={t("menu.loyaltyCoupons")}
      description={t("loyalty.coupons.description")}
      actions={() =>
        permissions.canManageCoupons ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("loyalty.coupons.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <>
          <CouponLookup />
          <CampaignList companyId={companyId} />
          <CouponCampaignDialog
            open={creating}
            onOpenChange={setCreating}
            companyId={companyId}
            campaign={null}
            onCreated={(campaign) =>
              void navigate({ to: "/sadakat/kuponlar/$id", params: { id: campaign.id } })
            }
          />
        </>
      )}
    </LoyaltyCompanyPage>
  );
}

function CouponLookup(): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const [text, setText] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"assign" | "cancel" | null>(null);
  const lookup = useCouponLookup(code);
  const notFound = isApiError(lookup.error, "NOT_FOUND");
  return (
    <Card className="grid gap-3 p-4">
      <form
        role="search"
        aria-label={t("loyalty.coupons.lookupTitle")}
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          const value = text.trim().toUpperCase();
          setCode(value.length >= 4 ? value : null);
        }}
      >
        <FormField label={t("loyalty.coupons.lookupLabel")} className="w-72 max-w-full">
          {(control) => (
            <Input
              {...control}
              className="font-mono uppercase"
              autoComplete="off"
              spellCheck={false}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          )}
        </FormField>
        <Button type="submit" variant="outline" loading={lookup.isFetching}>
          <Search aria-hidden="true" />
          {t("loyalty.coupons.lookup")}
        </Button>
      </form>
      {code === null ? null : notFound ? (
        <Alert tone="warning">{t("loyalty.coupons.lookupNotFound", { code })}</Alert>
      ) : lookup.error ? (
        <Alert tone="danger">{errorMessage(lookup.error)}</Alert>
      ) : lookup.data ? (
        <>
          <CouponDetailView
            coupon={lookup.data}
            canManage={permissions.canManageCoupons}
            onAssign={() => setDialog("assign")}
            onCancel={() => setDialog("cancel")}
          />
          <CouponCustomerDialog
            open={dialog === "assign"}
            onOpenChange={(next) => setDialog(next ? "assign" : null)}
            mode="assign"
            campaignId={null}
            coupon={lookup.data}
          />
          <CancelCouponDialog
            open={dialog === "cancel"}
            onOpenChange={(next) => setDialog(next ? "cancel" : null)}
            coupon={lookup.data}
          />
        </>
      ) : null}
    </Card>
  );
}

function validityText(campaign: CouponCampaign, openEnd: string): string {
  const from = formatTrDate(new Date(campaign.validFrom));
  return campaign.validUntil
    ? `${from} – ${formatTrDate(validToDate(campaign.validUntil))}`
    : `${from} – ${openEnd}`;
}

function CampaignList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const pages = useCursorPages(`${companyId}|${search.status ?? ""}`);
  const list = useCouponCampaigns(companyId, { status: search.status }, pages.cursor);

  const columns = useMemo<DataTableColumn<CouponCampaign>[]>(
    () => [
      {
        id: "code",
        header: t("loyalty.coupons.fields.templateCode"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => (
          <Link
            to="/sadakat/kuponlar/$id"
            params={{ id: row.id }}
            className="font-mono underline underline-offset-4"
          >
            {row.code}
          </Link>
        ),
      },
      {
        id: "name",
        header: t("loyalty.coupons.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => row.name,
      },
      {
        id: "campaign",
        header: t("loyalty.coupons.campaign"),
        exportValue: (row) => row.campaignCode,
        cell: (row) => (
          <span className="grid">
            <span className="font-mono text-xs">{row.campaignCode}</span>
            <span className="text-xs text-muted-foreground">{row.campaignName}</span>
          </span>
        ),
      },
      {
        id: "issuance",
        header: t("loyalty.coupons.fields.issuance"),
        exportValue: (row) => COUPON_ISSUANCE_LABELS[row.issuance],
        cell: (row) => (
          <span className="grid">
            <span>{COUPON_ISSUANCE_LABELS[row.issuance]}</span>
            {row.customerBound ? (
              <span className="text-xs text-muted-foreground">{t("loyalty.coupons.customerBound")}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "validity",
        header: t("loyalty.coupons.validity"),
        exportValue: (row) => validityText(row, t("loyalty.rules.openEnded")),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{validityText(row, t("loyalty.rules.openEnded"))}</span>
        ),
      },
      {
        id: "stats",
        header: t("loyalty.coupons.stats"),
        align: "right",
        exportValue: (row) => row.stats.total,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">
            {t("loyalty.coupons.statsText", {
              total: formatCount(row.stats.total),
              issued: formatCount(row.stats.issued),
              redeemed: formatCount(row.stats.redeemed),
            })}
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => COUPON_CAMPAIGN_STATUS_LABELS[row.status],
        cell: (row) => <CampaignStatusBadge status={row.status} />,
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("loyalty.coupons.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<TicketPercent />}
          title={t("loyalty.coupons.emptyTitle")}
          description={t("loyalty.coupons.emptyDescription")}
        />
      }
      filters={
        <FormField label={t("common.status")} className="w-48">
          {(control) => (
            <Select
              {...control}
              value={search.status ?? ""}
              onChange={(event) =>
                void navigate({
                  to: ".",
                  search: ((previous: Record<string, unknown>) =>
                    compactSearch({ ...previous, status: event.target.value })) as never,
                  replace: true,
                })
              }
            >
              <option value="">{t("common.all")}</option>
              {COUPON_CAMPAIGN_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {COUPON_CAMPAIGN_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
